import "./App.css";
import { Users, Target, TrendingUp } from "lucide-react";
import { ReactNode } from "react";

function DefaultAuthAside() {
  return (
    <div className="max-w-md space-y-8">
      <div>
        <img
          src="/graftus-logo.svg"
          alt="Graftus"
          className="h-10 w-auto"
        />
        <p className="mt-4 text-lg font-semibold tracking-tight text-foreground">
          AI-plattformen för säljorganisationer som vill driva intäkter
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Graftus samlar{" "}
          <span className="font-medium text-foreground">Recruit</span>{" "}
          och{" "}
          <span className="font-medium text-foreground">Leads</span> –
          rekrytering och leadgenerering med AI, byggt för team som ska växa
          affären.
        </p>
      </div>
      <ul className="space-y-4 text-sm text-muted-foreground">
        <li className="flex gap-3">
          <Users className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <span>
            <span className="font-medium text-foreground">Graftus Recruit</span>
            <span className="mt-0.5 block">
              AI-stödd rekrytering: annonser, matchningar och kandidatresor i ett
              flöde.
            </span>
          </span>
        </li>
        <li className="flex gap-3">
          <Target className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <span>
            <span className="font-medium text-foreground">Graftus Leads</span>
            <span className="mt-0.5 block">
              Hitta och kvalificera leads med hjälp av vårt nätverk så säljteamet fyller pipelinen.
            </span>
          </span>
        </li>
        <li className="flex gap-3">
          <TrendingUp className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <span>
            <span className="font-medium text-foreground">Mer säljarbete</span>
            <span className="mt-0.5 block">
              Mindre administration – mer fokus på det som driver omsättning.
            </span>
          </span>
        </li>
      </ul>
    </div>
  );
}

type UnAuthenticatedLayoutProps = {
  children: ReactNode;
  /** Left column on lg+; omit to use default marketing panel */
  aside?: ReactNode;
};

const UnAuthenticatedLayout = ({
  children,
  aside,
}: UnAuthenticatedLayoutProps) => {
  const asideContent = aside !== undefined ? aside : <DefaultAuthAside />;

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="grid min-h-dvh lg:grid-cols-2">
        <aside className="relative hidden overflow-hidden border-r border-border lg:flex lg:flex-col lg:justify-center lg:px-10 xl:px-14">
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.09] via-muted/70 to-primary/[0.04] dark:via-muted/50"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.45] dark:opacity-[0.28]"
            aria-hidden
            style={{
              backgroundImage:
                "radial-gradient(circle at center, hsl(var(--foreground) / 0.06) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
          <div
            className="pointer-events-none absolute -right-24 top-1/2 h-[min(80%,520px)] w-[min(80%,420px)] -translate-y-1/2 rounded-full bg-primary/[0.06] blur-3xl dark:bg-primary/[0.09]"
            aria-hidden
          />
          <div className="relative z-10 flex flex-col justify-center py-12">
            {asideContent}
          </div>
        </aside>
        <main className="flex h-dvh flex-1 items-start justify-center overflow-y-auto bg-background p-6">
          <div className="relative z-10 flex w-full justify-center my-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default UnAuthenticatedLayout;
export { DefaultAuthAside };
